/**
 * The 3D mesh scene manager: loads the baked GLB, renders with orbit/walk controls,
 * and provides visibility toggles for spatial kinds. Node names are origin_ids
 * from the scene bake (domain/amep/components/ifc/scene.py).
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { API_BASE } from '../../api';

export type ControlMode = 'orbit' | 'walk';
export type RungType = 'project' | 'site' | 'building' | 'storey' | 'space' | 'element';

export interface SpatialKind {
  name: string;
  ifcClass: string;
  visible: boolean;
  count: number;
  category?: string;
}

export interface Scope {
  rung: RungType;
  id: string;
}

export class MeshScene {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private root: THREE.Group;
  private orbitControls: OrbitControls;
  private walkControls: PointerLockControls;
  /** The walk-mode key listeners in force, so a toggle or dispose removes exactly the pair it added. */
  private walkKeyListeners: { onKeyDown: (e: KeyboardEvent) => void; onKeyUp: (e: KeyboardEvent) => void } | null = null;
  private controlMode: ControlMode = 'orbit';
  private animationFrame: number | null = null;
  private disposed = false;
  private glb: THREE.Group | null = null;
  private spatialKinds: Map<string, SpatialKind> = new Map();
  private nodesByIfcClass: Map<string, THREE.Object3D[]> = new Map();
  private nodesByOriginId: Map<string, THREE.Object3D> = new Map();
  private walkVelocity = new THREE.Vector3();
  private walkKeys: { [key: string]: boolean } = {};
  private readonly EYE_HEIGHT = 1.6; // meters

  constructor(canvas: HTMLCanvasElement) {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x111827);

    const width = canvas.clientWidth || 1024;
    const height = canvas.clientHeight || 768;

    this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1e6);
    this.renderer = new THREE.WebGLRenderer({ antialias: true, canvas });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);

    // Lighting
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const sun = new THREE.DirectionalLight(0xffffff, 1.2);
    sun.position.set(1, 2, 1.5);
    this.scene.add(sun);

    // Root group for scene hierarchy
    this.root = new THREE.Group();
    this.scene.add(this.root);

    // Orbit controls (default)
    this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement);
    this.orbitControls.autoRotate = false;

    // Walk controls
    this.walkControls = new PointerLockControls(this.camera, this.renderer.domElement);

    this.animate();
  }

  /**
   * Load the baked GLB scene from the server endpoint.
   * Node names in the GLB are origin_ids; we index them by IFC class from raw_attributes.
   */
  async load(projectId: string, authFetch: (url: string) => Promise<Response>): Promise<void> {
    if (this.disposed) throw new Error('Scene already disposed');

    try {
      const sceneUrl = `${API_BASE}/v1/viewer/scene?project_id=${encodeURIComponent(projectId)}`;
      const response = await authFetch(sceneUrl);

      if (!response.ok) {
        if (response.status === 404) {
          return; // No scene yet (honest absence)
        }
        throw new Error(`Scene load failed: HTTP ${response.status}`);
      }

      // Principle 8: fail loud, never a black screen. Refuse non-GLTF responses.
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('model/gltf-binary')) {
        throw new Error(
          `Expected model/gltf-binary, got ${contentType || 'no content-type header'}. ` +
          `Response was likely HTML (Vite/proxy misconfiguration). Check API_BASE and dev server config.`
        );
      }

      const arrayBuffer = await response.arrayBuffer();
      const loader = new GLTFLoader();

      // Parse the GLB (which is gltf binary)
      const gltf = await new Promise<{ scene: THREE.Group }>((resolve, reject) => {
        loader.parse(arrayBuffer, '', resolve, reject);
      });

      this.glb = gltf.scene;

      // Index nodes by origin_id and IFC class from the mesh names
      this.glb.traverse((obj) => {
        if (obj.name) {
          // Node name is origin_id
          this.nodesByOriginId.set(obj.name, obj);

          // Infer IFC class from object name
          const ifcClass = this.inferIfcClass(obj);
          if (ifcClass) {
            if (!this.nodesByIfcClass.has(ifcClass)) {
              this.nodesByIfcClass.set(ifcClass, []);
            }
            this.nodesByIfcClass.get(ifcClass)!.push(obj);
          }
        }
      });

      this.root.add(this.glb);
      this.frameAll();
    } catch (error) {
      throw new Error(`Failed to load scene: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Infer IFC class from object name or metadata.
   * The GLB nodes are named by origin_id, but we can look at metadata or
   * use heuristics. For now, we'll use a simple fallback.
   */
  private inferIfcClass(obj: THREE.Object3D): string | null {
    // TODO: integrate with ActiveGeoElement data to map origin_id -> IFC class
    // For now, use object name as a placeholder
    if (obj.name && obj.name.startsWith('Ifc')) {
      const match = obj.name.match(/^(Ifc[A-Za-z]+)/);
      if (match) return match[1];
    }
    return null;
  }

  /**
   * Classify GLB nodes using scope view data at depth "rows" or "whole".
   * Extracts IFC class from scope view rows/elements and populates spatialKinds.
   */
  classifyFromScope(scopeView: { depth: 'rows' | 'whole'; rows?: Array<{ origin_id: string; ifc_class: string; category?: string | null }>; elements?: Array<{ origin_id: string; raw_attributes?: Record<string, unknown> | null; category?: string | null }> }): void {
    this.spatialKinds.clear();

    const kindMap = new Map<string, { count: number; category?: string }>();

    // Classify from rows (depth "rows") or elements (depth "whole")
    const classified: Array<{ ifcClass: string | undefined; category: string | undefined }> =
      scopeView.depth === 'rows'
        ? (scopeView.rows ?? []).map((row) => ({
            ifcClass: row.ifc_class || undefined,
            category: row.category ?? undefined,
          }))
        : (scopeView.elements ?? []).map((el) => {
            const ifcClass = el.raw_attributes?.ifc_class;
            return {
              ifcClass: typeof ifcClass === 'string' && ifcClass !== '' ? ifcClass : undefined,
              category: el.category ?? undefined,
            };
          });

    for (const { ifcClass, category } of classified) {
      if (ifcClass) {
        const key = ifcClass;
        if (!kindMap.has(key)) {
          kindMap.set(key, { count: 0, category: category ?? undefined });
        }
        const kind = kindMap.get(key)!;
        kind.count += 1;
      }
    }

    // Register kinds with counts
    kindMap.forEach((data, ifcClass) => {
      this.spatialKinds.set(ifcClass, {
        name: this.displayNameFor(ifcClass),
        ifcClass,
        visible: true,
        count: data.count,
        category: data.category,
      });
    });
  }

  /**
   * Clear spatial kinds (used at depth "counts" where no per-class layers are shown).
   */
  clearSpatialKinds(): void {
    this.spatialKinds.clear();
  }

  /**
   * Display name for an IFC class.
   */
  private displayNameFor(ifcClass: string): string {
    // IfcFurnishingElement -> Furniture; IfcWall -> Wall, etc.
    if (ifcClass === 'IfcFurnishingElement') return 'Furniture';
    return ifcClass.replace(/^Ifc/, '').replace(/([A-Z])/g, ' $1').trim();
  }

  /**
   * Set visibility of objects by IFC class.
   */
  setVisible(ifcClass: string, visible: boolean): void {
    const nodes = this.nodesByIfcClass.get(ifcClass);
    if (nodes) {
      nodes.forEach((node) => {
        node.visible = visible;
      });
    }
    const kind = this.spatialKinds.get(ifcClass);
    if (kind) {
      kind.visible = visible;
    }
  }

  /**
   * Set visibility of objects by origin_id (the spatial rung at depth rows or whole).
   * Pass null to show everything (e.g., at depth counts).
   */
  setVisibilityByOriginIds(visibleIds: Set<string> | null): void {
    this.nodesByOriginId.forEach((node, originId) => {
      node.visible = visibleIds === null || visibleIds.has(originId);
    });
  }

  /**
   * Toggle control mode between orbit and walk.
   */
  setControlMode(mode: ControlMode): void {
    if (this.controlMode === mode) return;

    if (mode === 'orbit') {
      this.walkControls.unlock();
      this.orbitControls.enabled = true;
      this.controlMode = 'orbit';
    } else {
      this.orbitControls.enabled = false;
      this.walkControls.lock();
      this.controlMode = 'walk';
      this.setupWalkInput();
    }
  }

  /**
   * Setup keyboard and mouse input for walk mode.
   */
  private setupWalkInput(): void {
    // Toggling walk<->orbit calls this again; drop any listener pair from a previous call
    // before adding the new one, or each toggle leaks another pair that dispose() never sees.
    this.removeWalkInput();

    const onKeyDown = (e: KeyboardEvent) => {
      this.walkKeys[e.key.toLowerCase()] = true;
    };

    const onKeyUp = (e: KeyboardEvent) => {
      this.walkKeys[e.key.toLowerCase()] = false;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    this.walkKeyListeners = { onKeyDown, onKeyUp };
  }

  /** Remove the walk-mode key listeners in force, if any. */
  private removeWalkInput(): void {
    if (this.walkKeyListeners === null) return;
    window.removeEventListener('keydown', this.walkKeyListeners.onKeyDown);
    window.removeEventListener('keyup', this.walkKeyListeners.onKeyUp);
    this.walkKeyListeners = null;
  }

  /**
   * Get all spatial kinds present in the scene.
   */
  getSpatialKinds(): SpatialKind[] {
    return Array.from(this.spatialKinds.values());
  }

  /**
   * Frame the entire scene in view.
   */
  frameAll(): void {
    if (!this.glb) return;

    this.root.updateWorldMatrix(true, true);
    const bounds = new THREE.Box3().setFromObject(this.root);
    if (bounds.isEmpty()) return;

    const center = bounds.getCenter(new THREE.Vector3());
    const extent = bounds.getSize(new THREE.Vector3()).length() || 10;

    this.orbitControls.target.copy(center);
    this.camera.position.copy(center).add(
      new THREE.Vector3(extent * 0.6, extent * 0.5, extent * 0.6),
    );
    this.camera.near = extent / 1000;
    this.camera.far = extent * 10;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Animate: called every frame.
   */
  private animate = (): void => {
    if (this.disposed) return;

    this.animationFrame = requestAnimationFrame(this.animate);

    // Update walk mode movement
    if (this.controlMode === 'walk' && this.walkControls.isLocked) {
      const speed = 0.05; // meters per frame
      this.walkVelocity.set(0, 0, 0);
      if (this.walkKeys['w']) this.walkVelocity.z -= speed;
      if (this.walkKeys['a']) this.walkVelocity.x -= speed;
      if (this.walkKeys['s']) this.walkVelocity.z += speed;
      if (this.walkKeys['d']) this.walkVelocity.x += speed;
      this.walkControls.moveRight(this.walkVelocity.x);
      this.walkControls.moveForward(this.walkVelocity.z);
    }

    if (this.controlMode === 'orbit') {
      this.orbitControls.update();
    } else {
      this.walkControls.object.position.y = this.EYE_HEIGHT;
    }

    this.renderer.render(this.scene, this.camera);
  };

  /**
   * Handle window resize.
   */
  onWindowResize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  /**
   * Dispose of all resources.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
    }

    // Remove event listeners
    this.removeWalkInput();

    this.orbitControls.dispose();
    this.walkControls.dispose();

    this.root.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material.dispose();
        }
      }
    });

    this.renderer.dispose();
  }
}
